import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, AlertTriangle, Sparkles, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { SEO } from '../components/SEO';
import { MarketingNav, MarketingFooter } from '../components/MarketingLayout';
import { analytics } from '../hooks/useAnalytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SIGNALS = {
  narrative: 'Narrative Coherence',
  direction: 'Intellectual Direction',
  initiative: 'Initiative & Ownership',
  depth: 'Depth Over Breadth',
  voice: 'Authentic Voice',
  differentiation: 'Strategic Differentiation',
};

const QUESTIONS = [
  { signal: 'narrative', q: 'If an admissions reader saw only your activities list, would they understand what you care about?', options: ['No — it reads as unrelated items', "A few items connect; most don't", 'Mostly — one theme shows up repeatedly', 'Yes — it tells one unmistakable story'] },
  { signal: 'narrative', q: 'Can you state the central theme of your application in one sentence?', options: ['Not yet', 'Vaguely — it changes depending on the day', "Yes, but it sounds like everyone else's", 'Yes — specific, personal, and consistent'] },
  { signal: 'direction', q: 'How specific is your intended field of study?', options: ['Undecided', 'A broad area (e.g., "STEM" or "business")', 'A specific major', 'A specific major, plus questions I want to pursue inside it'] },
  { signal: 'direction', q: 'Outside of class, how often do you go deeper into your academic interest — reading, projects, courses?', options: ['Rarely', 'Occasionally, when assigned', 'Monthly, on my own', "Weekly — it's a habit"] },
  { signal: 'initiative', q: 'Have you started anything — a club, project, business, or research effort — rather than only joining?', options: ['Not yet', "I've helped run things others started", 'I started one small thing', 'I started something and grew it with measurable results'] },
  { signal: 'initiative', q: 'When something in your activities needs doing and nobody owns it, what happens?', options: ['I wait for direction', "I flag it to whoever's in charge", 'I sometimes step in', 'I take it, own it, and finish it'] },
  { signal: 'depth', q: 'What is your longest sustained commitment?', options: ['Under a year', '1–2 years', '2–3 years', '3+ years with growing responsibility'] },
  { signal: 'depth', q: 'Which best describes your activity list?', options: ['Many activities, shallow involvement', 'A mix — some real, some résumé filler', 'A few solid commitments', 'Few activities, deep ownership and results in each'] },
  { signal: 'voice', q: 'When you read your essay drafts aloud, do they sound like you?', options: ["I haven't drafted essays yet", 'They sound like a template', 'Mostly — with some borrowed polish', 'Yes — nobody else could have written them'] },
  { signal: 'voice', q: 'How much of your profile comes from genuine interest versus résumé-building?', options: ['Mostly résumé-building', 'Half and half', 'Mostly genuine, with some padding', 'Almost all genuine — the résumé followed the interest'] },
  { signal: 'differentiation', q: 'How does your profile differ from other strong applicants at your school?', options: ["I don't know", 'Better grades or scores, mostly', 'A distinct activity or two', 'A clearly different angle no one else at my school has'] },
  { signal: 'differentiation', q: "Do you know which \"lane\" your application occupies — the role you'd play in an admitted class?", options: ["I've never thought about it", 'I have a vague idea', 'Yes, roughly', 'Yes — and every part of my application supports it'] },
];

const STRENGTH_COPY = {
  narrative: 'Your activities and interests already point in one direction. That coherence is what lets a reader summarize you in a sentence — protect it as you add new commitments.',
  direction: 'You know where you are going academically, and you pursue it without being asked. Specific intellectual direction is one of the strongest signals selective committees reward.',
  initiative: "You build rather than join. Committees consistently distinguish founders and owners from members — your profile already carries that distinction.",
  depth: 'Your commitments are long and real. Depth is hard to fake and easy to verify through recommenders — it is a quiet but powerful asset.',
  voice: 'Your material sounds like you. In a pool of polished, interchangeable essays, an authentic voice is what gets remembered in committee.',
  differentiation: 'You understand your lane — the specific role you would play in an admitted class. That clarity should now shape every essay and activity description.',
};

const RISK_COPY = {
  narrative: 'Your application currently reads as separate accomplishments rather than one story. Readers spend minutes per file — a fragmented narrative is the most common reason strong students underperform.',
  direction: 'Top programs admit students who are going somewhere specific. Without a defined intellectual direction, your rigor and scores lose much of their persuasive power.',
  initiative: 'Selective committees distinguish students who build from students who join. Your profile needs at least one thing you started, owned, and grew.',
  depth: 'A long list of shallow commitments signals résumé-building. One or two deep, multi-year commitments outweigh ten memberships.',
  voice: 'Polished but generic essays are the fastest way to disappear in a competitive pool. Your strongest material is the part only you could write.',
  differentiation: 'If your application could belong to three other students at your school, the committee has no reason to choose yours. You need a defined lane.',
};

function getRecommendation(total) {
  if (total >= 27) {
    return {
      key: 'elite',
      label: 'Apply for Elite Consulting',
      reason: 'Your fundamentals are strong. At this level, the marginal gains come from expert positioning — the difference between a very good application and one a committee fights for. That is what Elite engagements are built to deliver.',
      to: '/consulting#elite',
      cta: 'Apply for Elite Consulting',
    };
  }
  if (total >= 16) {
    return {
      key: 'call',
      label: 'Book a Strategy Call',
      reason: 'You have real assets and real gaps. The highest-leverage next step is a strategy conversation: an honest read on your profile and a sequenced plan for the next two semesters.',
      to: '/consultation',
      cta: 'Book a Strategy Call',
    };
  }
  return {
    key: 'platform',
    label: 'Start with the Platform',
    reason: 'Your biggest gains right now come from structure: a coherent school list, organized essays, and tracked deadlines. The Ivy League Admissions Portal builds that foundation — and includes a 45-minute strategy session.',
    to: '/sign-up',
    cta: 'Start the $99 Platform',
  };
}

export default function DiagnosticPage() {
  const [step, setStep] = useState('intro'); // intro | quiz | freetext | results
  const [qIndex, setQIndex] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [freeText, setFreeText] = useState('');
  const [email, setEmail] = useState('');
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  const startQuiz = () => {
    analytics.diagnosticStart();
    setStep('quiz');
  };

  const answer = (score) => {
    const next = [...answers, score];
    setAnswers(next);
    if (qIndex + 1 < QUESTIONS.length) {
      setQIndex(qIndex + 1);
    } else {
      setStep('freetext');
    }
  };

  const computeResults = (allAnswers) => {
    const signals = {};
    Object.keys(SIGNALS).forEach(k => { signals[k] = 0; });
    QUESTIONS.forEach((q, i) => { signals[q.signal] += allAnswers[i] ?? 0; });
    const total = Object.values(signals).reduce((a, b) => a + b, 0);
    return { signals, total };
  };

  const finish = async (skipAi = false) => {
    const { signals, total } = computeResults(answers);
    const rec = getRecommendation(total);
    analytics.diagnosticComplete(rec.key, total);
    setStep('results');

    const payload = {
      signals,
      total,
      recommendation: rec.label,
      free_text: skipAi ? null : (freeText.trim() || null),
      email: email.trim() || null,
    };
    if (payload.free_text) setAiLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/diagnostic/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.analysis) setAiAnalysis(data.analysis);
    } catch { /* analysis is optional */ }
    setAiLoading(false);
  };

  const { signals, total } = step === 'results' ? computeResults(answers) : { signals: {}, total: 0 };
  const rec = step === 'results' ? getRecommendation(total) : null;
  const entries = Object.entries(signals);
  const strongest = entries.length ? entries.reduce((a, b) => (b[1] > a[1] ? b : a)) : null;
  const weakest = entries.length ? entries.reduce((a, b) => (b[1] < a[1] ? b : a)) : null;

  const fireRecEvent = () => {
    if (!rec) return;
    if (rec.key === 'platform') analytics.ctaPlatformStart('diagnostic-results');
    else if (rec.key === 'elite') analytics.ctaEliteApply('diagnostic-results');
    else analytics.ctaStrategyCall('diagnostic-results');
  };

  return (
    <div className="min-h-screen bg-white" data-testid="diagnostic-page">
      <SEO
        title="Ivy Signals Diagnostic — Free Ivy League Admissions Quiz"
        description="Free 10-minute Ivy League readiness diagnostic. Score six signals — narrative, direction, initiative, depth, voice, differentiation — and get an AI-strategist's read on your strongest asset and biggest risk."
        keywords="ivy league admissions diagnostic, college admissions quiz, ivy league readiness, ivy chances calculator"
      />
      <MarketingNav />

      <div className="pt-16">
        {/* INTRO */}
        {step === 'intro' && (
          <>
            <section className="bg-[#0F392B] text-white">
              <div className="max-w-3xl mx-auto px-4 py-20 text-center">
                <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-4">The Ivy Signals Diagnostic</p>
                <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.1] mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Where does your application <span className="italic text-[#D4AF37]">stand today?</span>
                </h1>
                <p className="text-base md:text-lg text-white/70 max-w-xl mx-auto leading-relaxed mb-8">
                  Twelve questions across the six signals selective committees actually weigh. Ten minutes. An honest read — not a sales pitch.
                </p>
                <Button size="lg" onClick={startQuiz} className="bg-[#D4AF37] text-[#0F392B] hover:bg-[#D4AF37]/90 h-13 px-10 text-base font-semibold shadow-xl" data-testid="diagnostic-start-btn">
                  Begin the Diagnostic <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            </section>
            <section className="py-16">
              <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                <h2 className="text-center text-2xl font-medium mb-10" style={{ fontFamily: "'Playfair Display', serif" }}>The six signals we score</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {Object.entries(SIGNALS).map(([k, label], i) => (
                    <div key={k} className="bg-[#F9F9F7] border border-[#E2E2E2] rounded-xl p-5 text-center" data-testid={`signal-card-${k}`}>
                      <div className="w-8 h-8 rounded-full bg-[#0F392B] flex items-center justify-center mx-auto mb-3">
                        <span className="text-xs font-bold text-[#D4AF37]">{i + 1}</span>
                      </div>
                      <p className="text-sm font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>{label}</p>
                    </div>
                  ))}
                </div>
                <p className="text-center text-xs text-muted-foreground mt-8 max-w-md mx-auto">
                  You'll receive your strongest signal, your biggest narrative risk, and the next step that actually fits your situation — the platform, a strategy call, or elite consulting.
                </p>
              </div>
            </section>
          </>
        )}

        {/* QUIZ */}
        {step === 'quiz' && (
          <section className="py-16 min-h-[70vh]">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-medium text-[#D4AF37] uppercase tracking-widest">{SIGNALS[QUESTIONS[qIndex].signal]}</p>
                <p className="text-xs text-muted-foreground" data-testid="diagnostic-progress">{qIndex + 1} / {QUESTIONS.length}</p>
              </div>
              <div className="w-full h-1.5 bg-[#E2E2E2] rounded-full mb-10">
                <div className="h-1.5 bg-[#0F392B] rounded-full transition-all duration-300" style={{ width: `${((qIndex) / QUESTIONS.length) * 100}%` }} />
              </div>
              <h2 className="text-xl md:text-2xl font-medium mb-8 leading-snug" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="diagnostic-question">
                {QUESTIONS[qIndex].q}
              </h2>
              <div className="space-y-3">
                {QUESTIONS[qIndex].options.map((opt, i) => (
                  <button
                    key={i}
                    onClick={() => answer(i)}
                    className="w-full text-left p-4 rounded-xl border border-[#E2E2E2] bg-white hover:border-[#0F392B] hover:bg-[#F9F9F7] transition-all text-sm"
                    data-testid={`diagnostic-option-${i}`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FREE TEXT */}
        {step === 'freetext' && (
          <section className="py-16 min-h-[70vh]">
            <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
              <p className="text-xs font-medium text-[#D4AF37] uppercase tracking-widest mb-3">Optional — Strategist's Note</p>
              <h2 className="text-xl md:text-2xl font-medium mb-3 leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>
                In two or three sentences: what's the thread that connects your strongest activities and your intended major?
              </h2>
              <p className="text-sm text-muted-foreground mb-6">Add this and our AI strategist writes a personalized note on your strongest asset, your biggest risk, and your next move.</p>
              <Textarea
                value={freeText}
                onChange={(e) => setFreeText(e.target.value)}
                placeholder="e.g., I started a tutoring nonprofit after watching my younger brother struggle with dyslexia, which led me toward cognitive science…"
                className="min-h-[120px] mb-4"
                data-testid="diagnostic-freetext-input"
              />
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email (optional — we'll send your full report to your inbox)"
                className="mb-6"
                data-testid="diagnostic-email-input"
              />
              <div className="flex flex-col sm:flex-row gap-3">
                <Button onClick={() => finish(false)} className="bg-[#0F392B] hover:bg-[#0F392B]/90 text-white h-12 px-8 font-semibold" data-testid="diagnostic-get-results-btn">
                  Get My Results <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button variant="ghost" onClick={() => finish(true)} className="h-12 px-6 text-sm" data-testid="diagnostic-skip-btn">
                  Skip and show results
                </Button>
              </div>
            </div>
          </section>
        )}

        {/* RESULTS */}
        {step === 'results' && rec && (
          <section className="py-16">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-3">Your Ivy Signals Report</p>
                <h1 className="text-3xl md:text-4xl font-medium tracking-tight mb-2" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="diagnostic-results-title">
                  Overall signal strength: <span className="italic">{total}/36</span>
                </h1>
                <p className="text-sm text-muted-foreground">Scored across six signals selective committees weigh.</p>
              </div>

              {/* Signal bars */}
              <div className="space-y-4 mb-12" data-testid="diagnostic-signal-bars">
                {Object.entries(SIGNALS).map(([k, label]) => (
                  <div key={k}>
                    <div className="flex items-center justify-between mb-1.5">
                      <p className="text-sm font-medium">{label}</p>
                      <p className="text-xs text-muted-foreground">{signals[k]}/6</p>
                    </div>
                    <div className="w-full h-2 bg-[#E2E2E2] rounded-full">
                      <div className={`h-2 rounded-full transition-all duration-700 ${signals[k] >= 4 ? 'bg-[#0F392B]' : signals[k] >= 2 ? 'bg-[#D4AF37]' : 'bg-red-400'}`} style={{ width: `${(signals[k] / 6) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Strongest / Risk */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
                <div className="bg-[#0F392B] rounded-2xl p-7 text-white" data-testid="diagnostic-strongest">
                  <CheckCircle className="h-5 w-5 text-[#D4AF37] mb-3" />
                  <p className="text-[10px] font-medium text-[#D4AF37] uppercase tracking-widest mb-1">Strongest Signal</p>
                  <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{SIGNALS[strongest[0]]}</h3>
                  <p className="text-xs text-white/70 leading-relaxed">{STRENGTH_COPY[strongest[0]]}</p>
                </div>
                <div className="bg-white border-2 border-[#D4AF37] rounded-2xl p-7" data-testid="diagnostic-risk">
                  <AlertTriangle className="h-5 w-5 text-[#D4AF37] mb-3" />
                  <p className="text-[10px] font-medium text-[#D4AF37] uppercase tracking-widest mb-1">Biggest Narrative Risk</p>
                  {weakest[1] >= 5 ? (
                    <>
                      <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Complacency</h3>
                      <p className="text-xs text-muted-foreground leading-relaxed">No critical weakness detected across the six signals. At this level, the risk is coasting: strong profiles lose to equally strong profiles that are positioned with more precision. Protect your lead with expert-level sequencing and positioning.</p>
                    </>
                  ) : (
                    <>
                      <h3 className="text-lg font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{SIGNALS[weakest[0]]}</h3>
                      <p className="text-xs text-muted-foreground leading-relaxed">{RISK_COPY[weakest[0]]}</p>
                    </>
                  )}
                </div>
              </div>

              {/* AI Analysis */}
              {(aiLoading || aiAnalysis) && (
                <div className="bg-[#F9F9F7] border border-[#E2E2E2] rounded-2xl p-7 mb-10" data-testid="diagnostic-ai-analysis">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="h-4 w-4 text-[#D4AF37]" />
                    <p className="text-[10px] font-medium text-[#D4AF37] uppercase tracking-widest">Strategist's Note</p>
                  </div>
                  {aiLoading ? (
                    <div className="flex items-center gap-3 text-sm text-muted-foreground py-4">
                      <Loader2 className="h-4 w-4 animate-spin" /> Writing your personalized note…
                    </div>
                  ) : (
                    <p className="text-sm text-foreground/85 leading-relaxed whitespace-pre-line">{aiAnalysis}</p>
                  )}
                </div>
              )}

              {/* Recommendation */}
              <div className="bg-[#0F392B] rounded-2xl p-8 text-center text-white" data-testid="diagnostic-recommendation">
                <p className="text-[10px] font-medium text-[#D4AF37] uppercase tracking-widest mb-2">Recommended Next Step</p>
                <h3 className="text-2xl font-semibold mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>{rec.label}</h3>
                <p className="text-sm text-white/70 leading-relaxed max-w-lg mx-auto mb-7">{rec.reason}</p>
                <Link to={rec.to} onClick={fireRecEvent}>
                  <Button size="lg" className="bg-[#D4AF37] text-[#0F392B] hover:bg-[#D4AF37]/90 h-12 px-10 font-semibold shadow-xl" data-testid="diagnostic-recommendation-cta">
                    {rec.cta} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </section>
        )}
      </div>

      <MarketingFooter />
    </div>
  );
}
